import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import { toKebab } from '@/lib/analytics-slugs'
import type { ConfidenceLabel } from '@/types/story'

/**
 * SP4 PR-2 T17 — `ConfidenceLadder`.
 *
 * Horizontal 100%-stacked bar, 5 segments in fixed confidence order
 * (established → corroborated → reported → single_source → unverified).
 * Clicking a segment navigates to `/stories?confidence=<kebab-slug>`.
 *
 * Palette maps to the shared chart-role tokens from the design playbook:
 *   established    → --chart-success-strong
 *   corroborated   → --chart-success
 *   reported       → --chart-caution
 *   single_source  → --chart-warning
 *   unverified     → --chart-muted
 *
 * Segments below the 12% width threshold hide their inline label but stay
 * clickable; the legend underneath always shows all 5 labels for context.
 */

const COLOUR: Record<ConfidenceLabel, string> = {
  established: 'var(--chart-success-strong)',
  corroborated: 'var(--chart-success)',
  reported: 'var(--chart-caution)',
  single_source: 'var(--chart-warning)',
  unverified: 'var(--chart-muted)',
}

const LABEL: Record<ConfidenceLabel, string> = {
  established: 'Established',
  corroborated: 'Corroborated',
  reported: 'Reported',
  single_source: 'Single-source',
  unverified: 'Unverified',
}

const ORDER: ConfidenceLabel[] = [
  'established',
  'corroborated',
  'reported',
  'single_source',
  'unverified',
]

export interface ConfidenceLadderProps {
  data: Array<{ label: ConfidenceLabel; count: number }>
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

export function ConfidenceLadder({
  data,
  isLoading,
  isError,
  onRetry,
}: ConfidenceLadderProps) {
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Confidence ladder</h3>
        <Skeleton data-testid="sp4-confidence-skeleton" className="h-10 w-full" />
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Confidence ladder</h3>
        <ErrorState
          title="Couldn't load confidence data."
          onRetry={onRetry}
        />
      </Card>
    )
  }

  const total = data.reduce((s, d) => s + d.count, 0)
  if (total === 0) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Confidence ladder</h3>
        <p className="text-sm text-muted-foreground">
          No stories in the selected range.
        </p>
      </Card>
    )
  }

  const byLabel = Object.fromEntries(
    data.map((d) => [d.label, d.count]),
  ) as Record<ConfidenceLabel, number>

  return (
    <Card className="p-4">
      <h3 className="mb-3 text-sm font-semibold">Confidence ladder</h3>
      <div
        className="flex h-10 w-full overflow-hidden rounded-md"
        role="group"
        aria-label="Confidence distribution"
      >
        {ORDER.map((label) => {
          const count = byLabel[label] ?? 0
          const pct = total > 0 ? (count / total) * 100 : 0
          if (pct === 0) return null
          return (
            <button
              key={label}
              type="button"
              data-testid={`sp4-confidence-segment-${label}`}
              onClick={() => navigate(`/stories?confidence=${toKebab(label)}`)}
              style={{ width: `${pct}%`, backgroundColor: COLOUR[label] }}
              className="flex items-center justify-center text-xs text-white transition hover:brightness-110"
              aria-label={`${LABEL[label]} — ${count} stories (${Math.round(pct)}%)`}
            >
              {pct > 12 ? `${LABEL[label]} ${Math.round(pct)}%` : ''}
            </button>
          )
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {ORDER.map((label) => (
          <span key={label} className="inline-flex items-center gap-1">
            <span
              aria-hidden
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: COLOUR[label] }}
            />
            {LABEL[label]}
          </span>
        ))}
      </div>
    </Card>
  )
}
